import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import AdminDashboard from "@/AdminDashboard";
import APPLIANCES_FALLBACK from "@/data/appliances_fallback.json";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BRAND = "#0047d6";
const BRAND_CYAN = "#45b2ff";

// Meta Pixel — safe wrapper. No-ops when fbq isn't loaded (ad blockers, dev, SSR).
function fbqTrack(event, params) {
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", event, params || {});
    }
  } catch (_) { /* never crash the app on analytics */ }
}
const ACCENT = "#ff8e60";

// ============== Utility ==============
function Stepper({ step }) {
  const steps = ["Appliances", "Solar & backup", "Recommendations"];
  return (
    <ol
      className="mx-auto flex max-w-2xl items-center justify-center gap-2 sm:gap-4"
      data-testid="stepper"
    >
      {steps.map((label, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <li key={label} className="flex flex-1 items-center gap-2">
            <div
              data-testid={`stepper-node-${n}`}
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition ${
                done
                  ? "bg-blue-600 text-white"
                  : active
                    ? "text-white ring-4 ring-blue-100"
                    : "bg-slate-200 text-slate-500"
              }`}
              style={active ? { background: `linear-gradient(135deg, ${BRAND}, ${BRAND_CYAN})` } : undefined}
            >
              {done ? "✓" : n}
            </div>
            <span
              className={`hidden text-sm font-medium sm:inline ${
                active ? "text-slate-900" : "text-slate-500"
              }`}
            >
              {label}
            </span>
            {n < steps.length && (
              <div
                className={`h-0.5 flex-1 ${done ? "bg-blue-600" : "bg-slate-200"}`}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function fmtWh(wh) {
  if (wh >= 1000) return `${(wh / 1000).toFixed(wh >= 10000 ? 0 : 1)} kWh`;
  return `${Math.round(wh)} Wh`;
}

function fmtMoney(amount, currency = "USD") {
  if (amount == null) return "—";
  const n = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: n % 1 === 0 ? 0 : 2,
  }).format(n);
}

// ============== Step 1: Appliances (vertical guided flow) ==============
const SECTION_ORDER = ["kitchen", "climate", "medical", "lighting", "electronics", "tools", "pumps"];

function ApplianceStep({ groups, selections, setSelections, onNext, onOpenCustom }) {
  // Sort groups using SECTION_ORDER but keep any that aren't listed at the end.
  const sortedGroups = useMemo(() => {
    const idx = (cat) => {
      const i = SECTION_ORDER.indexOf(cat);
      return i === -1 ? 999 : i;
    };
    return [...groups].sort((a, b) => idx(a.category) - idx(b.category));
  }, [groups]);

  // Refs for smooth scrolling between sections + sticky-bar offset awareness.
  const sectionRefs = useMemo(() => {
    const m = {};
    sortedGroups.forEach((g) => { m[g.category] = { current: null }; });
    return m;
  }, [sortedGroups]);
  const setSectionRef = useCallback((cat) => (el) => {
    if (sectionRefs[cat]) sectionRefs[cat].current = el;
  }, [sectionRefs]);

  // Track which section is currently in view to know what "Continue" should do.
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        visible.sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        const cat = visible[0].target.getAttribute("data-cat");
        const i = sortedGroups.findIndex((g) => g.category === cat);
        if (i >= 0) setActiveIdx(i);
      },
      { rootMargin: "-30% 0px -50% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    sortedGroups.forEach((g) => {
      const el = sectionRefs[g.category]?.current;
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [sortedGroups, sectionRefs]);

  // Mobile collapse state (only sections with no selections collapse by default).
  const [mobileExpanded, setMobileExpanded] = useState({});
  function toggleMobile(cat) {
    setMobileExpanded((p) => ({ ...p, [cat]: !p[cat] }));
  }

  function update(key, patch) {
    setSelections((prev) => {
      const next = { ...prev };
      const cur = next[key] || { quantity: 0, hours_per_day: patch.hours_per_day ?? 0 };
      next[key] = { ...cur, ...patch };
      if (next[key].quantity <= 0) delete next[key];
      return next;
    });
  }

  // Per-category selected count (presets only) for collapsing.
  const selectedByCat = useMemo(() => {
    const m = {};
    sortedGroups.forEach((g) => {
      m[g.category] = g.items.reduce((acc, a) => acc + ((selections[a.key]?.quantity || 0) > 0 ? 1 : 0), 0);
    });
    return m;
  }, [sortedGroups, selections]);

  const totals = useMemo(() => {
    let dailyWh = 0;
    let peakW = 0;
    Object.entries(selections).forEach(([k, v]) => {
      if (k.startsWith("__custom__")) return;
      const preset = sortedGroups.flatMap((g) => g.items).find((a) => a.key === k);
      if (!preset) return;
      dailyWh += preset.watts * v.quantity * v.hours_per_day;
      peakW += preset.watts * v.quantity;
    });
    Object.values(selections).forEach((v) => {
      if (!v.custom) return;
      dailyWh += (v.watts || 0) * (v.quantity || 1) * (v.hours_per_day || 0);
      peakW += (v.watts || 0) * (v.quantity || 1);
    });
    return { dailyWh, peakW };
  }, [selections, sortedGroups]);

  const totalSelectedItems = Object.values(selections).reduce(
    (a, v) => a + (v.quantity > 0 ? 1 : 0),
    0,
  );
  const canAdvance = totalSelectedItems > 0;
  const customEntries = Object.entries(selections).filter(([, v]) => v.custom);

  // "Continue" CTA logic:
  //   - No selections → disabled
  //   - Not last section → scroll to next section
  //   - Last section OR ≥3 selected → "See my results" → onNext()
  const atLastSection = activeIdx >= sortedGroups.length - 1;
  const readyForResults = atLastSection || totalSelectedItems >= 3;
  const continueLabel = readyForResults ? "See my results →" : "Continue →";

  function handleContinue() {
    if (!canAdvance) return;
    if (readyForResults) {
      onNext();
      return;
    }
    const next = sortedGroups[Math.min(activeIdx + 1, sortedGroups.length - 1)];
    const el = sectionRefs[next.category]?.current;
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div className="space-y-6 pb-24" data-testid="step-1">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">What do you need to power?</h2>
          <p className="mt-1 text-sm text-slate-500">
            Tell us what you want to back up. Add as many or as few items as you'd like — scroll, or use Continue.
          </p>
        </div>
        {canAdvance && (
          <button
            data-testid="skip-to-results"
            onClick={onNext}
            className="hidden shrink-0 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 sm:inline-block"
          >
            Skip to results →
          </button>
        )}
      </div>

      <div className="space-y-10">
        {sortedGroups.map((g, gi) => {
          const selectedCount = selectedByCat[g.category] || 0;
          const explicitlyExpanded = mobileExpanded[g.category];
          const mobileOpen = selectedCount > 0 || explicitlyExpanded;
          return (
            <section
              key={g.category}
              data-cat={g.category}
              data-testid={`section-${g.category}`}
              ref={setSectionRef(g.category)}
              className="scroll-mt-24"
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <h3 className="text-lg font-bold text-slate-900 sm:text-xl">
                  <span className="text-slate-400">{String(gi + 1).padStart(2, "0")}.</span>{" "}
                  {g.label}
                </h3>
                {selectedCount > 0 && (
                  <span
                    data-testid={`section-count-${g.category}`}
                    className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700"
                  >
                    {selectedCount} selected
                  </span>
                )}
              </div>
              {/* Mobile collapsed prompt */}
              {!mobileOpen && (
                <button
                  data-testid={`section-expand-${g.category}`}
                  onClick={() => toggleMobile(g.category)}
                  className="block w-full rounded-lg border border-dashed border-slate-300 bg-white py-4 text-center text-sm text-slate-500 hover:border-blue-400 hover:text-blue-700 sm:hidden"
                >
                  Tap to expand · {g.items.length} items
                </button>
              )}
              <div className={`${mobileOpen ? "" : "hidden"} grid grid-cols-1 gap-3 sm:!grid sm:grid-cols-2`}>
                {g.items.map((a) => {
                  const sel = selections[a.key];
                  const qty = sel?.quantity || 0;
                  const hours = sel?.hours_per_day ?? a.default_hours_per_day ?? 0;
                  const selected = qty > 0;
                  return (
                    <div
                      key={a.key}
                      data-testid={`appliance-card-${a.key}`}
                      className={`rounded-lg border p-4 transition ${
                        selected
                          ? "border-blue-600 bg-blue-50/40 ring-2 ring-blue-100"
                          : "border-slate-200 bg-white opacity-90 hover:opacity-100"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-semibold text-slate-900">{a.label}</div>
                          <div className="text-xs text-slate-500">{a.watts}W{a.surge_watts ? ` • surge ${a.surge_watts}W` : ""}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            data-testid={`dec-${a.key}`}
                            onClick={() => update(a.key, { quantity: Math.max(0, qty - 1), hours_per_day: hours })}
                            className="h-7 w-7 rounded border border-slate-300 text-slate-700 hover:bg-slate-100"
                            aria-label="decrease"
                          >−</button>
                          <span className="w-6 text-center text-sm font-semibold tabular-nums" data-testid={`qty-${a.key}`}>{qty}</span>
                          <button
                            data-testid={`inc-${a.key}`}
                            onClick={() => update(a.key, { quantity: qty + 1, hours_per_day: hours || a.default_hours_per_day })}
                            className="h-7 w-7 rounded text-white"
                            style={{ background: `linear-gradient(135deg, ${BRAND}, ${BRAND_CYAN})` }}
                            aria-label="increase"
                          >+</button>
                        </div>
                      </div>
                      {selected && (
                        <div className="mt-3">
                          <div className="mb-1 flex items-center justify-between text-xs text-slate-600">
                            <span>Hours per day</span>
                            <span className="font-semibold text-slate-900">{hours}h</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="24"
                            step="0.25"
                            value={hours}
                            data-testid={`hours-${a.key}`}
                            onChange={(e) => update(a.key, { quantity: qty, hours_per_day: parseFloat(e.target.value) })}
                            className="w-full accent-blue-600"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}

        {customEntries.length > 0 && (
          <section className="space-y-2" data-testid="section-custom">
            <h3 className="text-lg font-bold text-slate-900">Custom loads</h3>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {customEntries.map(([k, v]) => (
                <div key={k} data-testid={`custom-row-${k}`} className="flex items-center justify-between rounded-lg border border-blue-300 bg-blue-50/40 p-3 text-sm">
                  <div>
                    <div className="font-semibold text-slate-900">{v.label}</div>
                    <div className="text-xs text-slate-500">{v.watts}W × {v.quantity} @ {v.hours_per_day}h/day</div>
                  </div>
                  <button
                    data-testid={`remove-custom-${k}`}
                    className="text-xs font-medium text-slate-500 hover:text-red-600"
                    onClick={() => setSelections((prev) => { const n = { ...prev }; delete n[k]; return n; })}
                  >Remove</button>
                </div>
              ))}
            </div>
          </section>
        )}

        <button
          data-testid="add-custom-btn"
          onClick={onOpenCustom}
          className="block w-full rounded-lg border border-dashed border-slate-300 bg-white py-5 text-sm font-medium text-slate-600 hover:border-blue-400 hover:text-blue-700"
        >
          + Add custom appliance
        </button>
      </div>

      {/* Sticky bottom action bar */}
      <div
        data-testid="running-total"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur"
      >
        <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex gap-6 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Daily energy</div>
              <div data-testid="total-daily-wh" className="text-base font-bold sm:text-lg" style={{ color: BRAND }}>{fmtWh(totals.dailyWh)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Peak continuous</div>
              <div data-testid="total-peak-w" className="text-base font-bold text-slate-900 sm:text-lg">{Math.round(totals.peakW)} W</div>
            </div>
            <div className="hidden sm:block">
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Items</div>
              <div className="text-base font-bold text-slate-900 sm:text-lg" data-testid="total-items">{totalSelectedItems}</div>
            </div>
          </div>
          <button
            data-testid="next-btn"
            disabled={!canAdvance}
            onClick={handleContinue}
            className="rounded-md px-6 py-2.5 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:bg-slate-300"
            style={!canAdvance ? undefined : { background: `linear-gradient(135deg, ${BRAND}, ${BRAND_CYAN})` }}
          >{continueLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ============== Custom modal ==============
function CustomModal({ open, onClose, onAdd }) {
  const [label, setLabel] = useState("");
  const [watts, setWatts] = useState("");
  const [hours, setHours] = useState("1");
  useEffect(() => { if (open) { setLabel(""); setWatts(""); setHours("1"); } }, [open]);
  if (!open) return null;
  const canAdd = label.trim() && parseFloat(watts) > 0 && parseFloat(hours) >= 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="custom-modal">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-bold text-slate-900">Add custom appliance</h3>
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Label</span>
            <input data-testid="custom-label" className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Aquarium pump" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Watts</span>
              <input data-testid="custom-watts" type="number" min="1" className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" value={watts} onChange={(e) => setWatts(e.target.value)} placeholder="80" />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Hours/day</span>
              <input data-testid="custom-hours" type="number" min="0" max="24" step="0.25" className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200" value={hours} onChange={(e) => setHours(e.target.value)} />
            </label>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button data-testid="custom-cancel" onClick={onClose} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
          <button
            data-testid="custom-add"
            disabled={!canAdd}
            onClick={() => { onAdd({ label: label.trim(), watts: parseFloat(watts), hours_per_day: parseFloat(hours) }); onClose(); }}
            className="rounded-md px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
            style={!canAdd ? undefined : { background: `linear-gradient(135deg, ${BRAND}, ${BRAND_CYAN})` }}
          >Add</button>
        </div>
      </div>
    </div>
  );
}

// ============== Step 2 ==============
function SolarStep({ sunHours, setSunHours, backupDays, setBackupDays, onBack, onCalc, calculating }) {
  return (
    <div className="space-y-8" data-testid="step-2">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">Solar & backup needs</h2>
        <p className="mt-1 text-sm text-slate-500">These shape how much capacity and solar wattage we recommend.</p>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="mb-2 flex items-center justify-between">
          <label className="text-sm font-semibold text-slate-800">Average sun hours / day</label>
          <span data-testid="sun-hours-value" className="text-lg font-bold" style={{ color: BRAND }}>{sunHours}h</span>
        </div>
        <input data-testid="sun-hours" type="range" min="2" max="8" step="0.5" value={sunHours} onChange={(e) => setSunHours(parseFloat(e.target.value))} className="w-full accent-blue-600" />
        <div className="mt-1 flex justify-between text-xs text-slate-400"><span>2h (cloudy)</span><span>5h (avg)</span><span>8h (sunny)</span></div>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="mb-2 flex items-center justify-between">
          <label className="text-sm font-semibold text-slate-800">Days of backup without sun</label>
          <span data-testid="backup-days-value" className="text-lg font-bold" style={{ color: BRAND }}>{backupDays} day{backupDays === 1 ? "" : "s"}</span>
        </div>
        <input data-testid="backup-days" type="range" min="1" max="7" step="1" value={backupDays} onChange={(e) => setBackupDays(parseFloat(e.target.value))} className="w-full accent-blue-600" />
        <div className="mt-1 flex justify-between text-xs text-slate-400"><span>1 day</span><span>3 days</span><span>7 days</span></div>
      </div>
      <div className="flex items-center justify-between">
        <button data-testid="back-btn" onClick={onBack} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">← Back</button>
        <button
          data-testid="calc-btn"
          disabled={calculating}
          onClick={onCalc}
          className="rounded-md px-6 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-400"
          style={calculating ? undefined : { background: `linear-gradient(135deg, ${BRAND}, ${BRAND_CYAN})` }}
        >{calculating ? "Calculating…" : "Calculate →"}</button>
      </div>
    </div>
  );
}

// ============== Tier card ==============
function BundleRow({ role, testid }) {
  if (!role) return null;
  if (role.match_failure_reason) {
    return (
      <div data-testid={testid} className="flex items-start gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
        <span className="mt-0.5">⚠</span>
        <span>{role.match_failure_reason}{role.quantity ? ` (needed × ${role.quantity})` : ""}</span>
      </div>
    );
  }
  return (
    <div data-testid={testid} className="flex items-center gap-3 rounded-md border border-slate-100 bg-slate-50 p-2">
      {role.image ? (
        <img src={role.image} alt="" className="h-10 w-10 shrink-0 rounded object-cover" />
      ) : (
        <div className="h-10 w-10 shrink-0 rounded bg-slate-200" />
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-slate-900">{role.title}</div>
        <div className="text-[11px] text-slate-500">× {role.quantity} · {fmtMoney(role.price, role.currency)} ea</div>
      </div>
      <div className="text-xs font-semibold tabular-nums text-slate-900">
        {fmtMoney((parseFloat(role.price) || 0) * role.quantity, role.currency)}
      </div>
    </div>
  );
}

function LeadForm({ tierKey, model, defaultEmail, onSubmit, status }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [open, setOpen] = useState(false);
  if (status === "done") {
    return (
      <div
        data-testid={`lead-success-${tierKey}`}
        className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800"
      >
        ✓ We'll email you when {model} is back in stock — thanks!
      </div>
    );
  }
  if (!open) {
    return (
      <button
        data-testid={`lead-open-${tierKey}`}
        onClick={() => setOpen(true)}
        className="mt-auto rounded-md px-4 py-2.5 text-sm font-semibold text-white"
        style={{ background: `linear-gradient(135deg, ${BRAND}, ${BRAND_CYAN})` }}
      >
        Notify me when this bundle is in stock
      </button>
    );
  }
  return (
    <div className="space-y-2" data-testid={`lead-form-${tierKey}`}>
      <input
        type="email"
        data-testid={`lead-email-${tierKey}`}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
      />
      <div className="flex gap-2">
        <button
          data-testid={`lead-submit-${tierKey}`}
          disabled={status === "submitting" || !email}
          onClick={() => onSubmit(tierKey, email)}
          className="flex-1 rounded-md px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
          style={status === "submitting" || !email ? undefined : { background: `linear-gradient(135deg, ${BRAND}, ${BRAND_CYAN})` }}
        >
          {status === "submitting" ? "Submitting…" : "Notify me"}
        </button>
        <button
          data-testid={`lead-cancel-${tierKey}`}
          onClick={() => setOpen(false)}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
        >Cancel</button>
      </div>
      {status === "error" && (
        <div data-testid={`lead-error-${tierKey}`} className="text-xs text-red-600">
          Something went wrong. Please check your email and try again.
        </div>
      )}
    </div>
  );
}

// Lazy-loaded once across the SPA so subsequent modal opens are instant.
let _ghlEmbedScriptPromise = null;
function loadGhlEmbedScript() {
  if (_ghlEmbedScriptPromise) return _ghlEmbedScriptPromise;
  _ghlEmbedScriptPromise = new Promise((resolve) => {
    if (document.querySelector('script[data-ghl-embed]')) { resolve(); return; }
    const s = document.createElement("script");
    s.src = "https://link.msgsndr.com/js/form_embed.js";
    s.async = true;
    s.dataset.ghlEmbed = "1";
    s.onload = () => resolve();
    s.onerror = () => resolve(); // fail silently — iframe still loads
    document.head.appendChild(s);
  });
  return _ghlEmbedScriptPromise;
}

function ConsultModal({ open, onClose }) {
  const dialogRef = useRef(null);

  // Body scroll lock + ESC + focus trap (lightweight).
  useEffect(() => {
    if (!open) return;
    loadGhlEmbedScript();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab" && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll(
          'button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    document.addEventListener("keydown", onKey);
    // initial focus on close button for screen readers
    setTimeout(() => dialogRef.current?.querySelector("[data-testid=consult-modal-close]")?.focus(), 50);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="consult-modal-title"
      data-testid="consult-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={dialogRef}
        className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div>
            <div className="mb-0.5 text-[10px] font-extrabold uppercase tracking-[0.18em] text-slate-500">
              Starpower Network
            </div>
            <h2 id="consult-modal-title" className="text-base font-bold text-slate-900 sm:text-lg">
              Schedule your EcoFlow Ocean Pro design consultation
            </h2>
            <p className="mt-0.5 text-xs text-slate-500 sm:text-sm">
              A Starpower designer will walk you through whole-home sizing, panel layout, and install logistics.
            </p>
          </div>
          <button
            data-testid="consult-modal-close"
            onClick={onClose}
            aria-label="Close"
            className="-mr-1 shrink-0 rounded-full p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto bg-slate-50">
          <iframe
            title="EcoFlow Ocean Pro consultation booking"
            src="https://api.leadconnectorhq.com/widget/booking/L5B0HitSdMplvqXOGutS"
            style={{ width: "100%", border: "none", overflow: "hidden", minHeight: "700px" }}
            scrolling="no"
            id="L5B0HitSdMplvqXOGutS_1780524090480"
            data-testid="consult-modal-iframe"
          />
        </div>
      </div>
    </div>
  );
}

const TIER_STYLES = {
  good:   { label: "GOOD",   sub: "Minimum viable",        header: "bg-slate-100 text-slate-700",   border: "border-slate-200", order: "order-2 lg:order-1" },
  better: { label: "BETTER", sub: "Comfortable headroom",  header: "text-white",                     border: "border-blue-600", featured: true, order: "order-1 lg:order-2" },
  best:   { label: "BEST",   sub: "Future-proof",          header: "text-white",                     border: "border-slate-800", order: "order-3 lg:order-3" },
};

function TierCard({ tierKey, data, onCheckout, checkingOut, onLeadSubmit, leadStatus, leadEmail, onOpenConsult }) {
  const cart = data?.cart || {};
  const ready = cart?.ready_for_checkout;
  const requiresConsult = !!cart?.requires_consultation;
  const s = TIER_STYLES[tierKey];
  const isBetter = tierKey === "better";
  const isBest = tierKey === "best";
  const noBundleAtAll =
    !cart || (!cart.station && !cart.extra_battery && !cart.solar_panel);
  const stationFailed = cart?.station?.match_failure_reason;

  return (
    <div
      data-testid={`tier-card-${tierKey}`}
      className={`flex flex-col overflow-hidden rounded-2xl border bg-white ${s.border} ${s.order} ${isBetter ? "shadow-lg ring-2 ring-blue-100" : "shadow-sm"}`}
    >
      <div
        className={`relative flex items-center justify-between px-5 py-3 ${s.header}`}
        style={
          isBetter ? { background: `linear-gradient(135deg, ${BRAND}, ${BRAND_CYAN})` } :
          isBest ? { background: `linear-gradient(135deg, #0f172a, #1e3a8a)` } : undefined
        }
      >
        <div>
          <div className="text-xs font-extrabold uppercase tracking-[0.18em]">{s.label}</div>
          <div className="text-[11px] opacity-90">{s.sub}</div>
        </div>
        {isBetter && (
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase"
            style={{ background: ACCENT, color: "#0f172a" }}
          >Recommended</span>
        )}
        {requiresConsult && (
          <span
            className="rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-900"
            data-testid={`consult-badge-${tierKey}`}
          >Whole-home</span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-4 p-5">
        <div>
          <div className="text-lg font-extrabold text-slate-900" data-testid={`tier-model-${tierKey}`}>
            {data?.model || "—"}
          </div>
          {!requiresConsult && data?.extra_batteries > 0 && (
            <div className="text-xs text-slate-500">+ {data.extra_batteries} extra {data.extra_batteries === 1 ? "battery" : "batteries"}</div>
          )}
        </div>

        <dl className="grid grid-cols-2 gap-y-1.5 text-sm">
          <dt className="text-slate-500">Capacity</dt>
          <dd className="text-right font-semibold text-slate-900">{data?.effective_capacity_wh != null ? fmtWh(data.effective_capacity_wh) : "—"}</dd>
          <dt className="text-slate-500">AC output</dt>
          <dd className="text-right font-semibold text-slate-900">{data?.ac_output_w != null ? `${data.ac_output_w}W` : "—"}{data?.x_boost_w ? ` (+X-Boost)` : ""}</dd>
          <dt className="text-slate-500">Solar input</dt>
          <dd className="text-right font-semibold text-slate-900">{data?.solar_w != null ? `${data.solar_w}W` : "—"}{data?.solar_capped ? " (capped)" : ""}</dd>
          <dt className="text-slate-500">Runtime</dt>
          <dd className="text-right font-semibold text-slate-900">~{data?.runtime_hours ?? "—"}h</dd>
        </dl>

        {data?.notes?.length > 0 && (
          <ul
            className="space-y-1 rounded-md bg-blue-50 p-2 text-xs text-blue-800"
            data-testid={`tier-notes-${tierKey}`}
          >
            {data.notes.map((n, i) => <li key={i}>ℹ {n}</li>)}
          </ul>
        )}

        {requiresConsult ? (
          <div
            data-testid={`consult-explainer-${tierKey}`}
            className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900"
          >
            <strong>Ocean Pro</strong> is a whole-home modular system (6–90 kWh capacity, up to 21.6 kW continuous).
            Final configuration depends on your panel layout, critical loads, and local code — our team designs every install.
          </div>
        ) : (
          <div className="rounded-lg bg-white p-0">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Bundle</div>
              {cart?.total_estimated && (
                <div className="text-sm font-extrabold text-slate-900">
                  {fmtMoney(cart.total_estimated, cart.currency)}
                </div>
              )}
            </div>
            {noBundleAtAll ? (
              <div
                data-testid={`bundle-unavailable-${tierKey}`}
                className="rounded-md bg-slate-50 p-3 text-xs text-slate-500"
              >
                Bundle data unavailable for this tier.
              </div>
            ) : (
              <div className="space-y-2" data-testid={`bundle-rows-${tierKey}`}>
                <BundleRow role={cart?.station} testid={`role-station-${tierKey}`} />
                <BundleRow role={cart?.extra_battery} testid={`role-battery-${tierKey}`} />
                <BundleRow role={cart?.solar_panel} testid={`role-panel-${tierKey}`} />
              </div>
            )}
          </div>
        )}

        {data?.warnings?.length > 0 && (
          <ul className="space-y-1 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
            {data.warnings.map((w, i) => <li key={i}>• {w}</li>)}
          </ul>
        )}

        {requiresConsult ? (
          <button
            data-testid={`consult-open-${tierKey}`}
            onClick={() => onOpenConsult(tierKey, data?.model)}
            className="mt-auto rounded-md px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow"
            style={{ background: "linear-gradient(135deg, #f97316, #f59e0b)" }}
          >
            Schedule Design Consultation
          </button>
        ) : ready ? (
          <button
            data-testid={`tier-cta-${tierKey}`}
            disabled={checkingOut}
            onClick={() => onCheckout(tierKey, cart)}
            className="mt-auto rounded-md px-4 py-2.5 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:bg-slate-300"
            style={!checkingOut ? { background: `linear-gradient(135deg, ${BRAND}, ${BRAND_CYAN})` } : undefined}
          >
            {checkingOut ? "Opening checkout…" : "Add bundle to cart"}
          </button>
        ) : (
          <div className="mt-auto">
            {stationFailed && (
              <div className="mb-2 text-[11px] text-slate-500">
                We don't carry {data?.model || "this model"} yet — leave your email and we'll let you know.
              </div>
            )}
            <LeadForm
              tierKey={tierKey}
              model={data?.model || "this bundle"}
              defaultEmail={leadEmail}
              onSubmit={onLeadSubmit}
              status={leadStatus}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ============== Step 3 ==============
function ResultsStep({ result, onRestart, onShare, shareState, onCheckout, checkoutState, onLeadSubmit, leadStatuses, leadEmail, onOpenConsult }) {
  if (!result) return null;
  return (
    <div className="space-y-6" data-testid="step-3">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">Your recommendations</h2>
        <p className="mt-1 text-sm text-slate-500">
          Based on <strong>{fmtWh(result.summary.total_daily_wh)}</strong> daily energy,{" "}
          <strong>{result.summary.peak_continuous_w}W</strong> peak, and{" "}
          <strong>{result.summary.backup_days} day(s)</strong> of backup at{" "}
          <strong>{result.summary.sun_hours}h</strong> of sun.
        </p>
      </div>

      {result.warnings_global?.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="global-warnings">
          {result.warnings_global.map((w, i) => <div key={i}>⚠ {w}</div>)}
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3" data-testid="tier-grid">
        <TierCard tierKey="good"   data={result.tiers?.good}   onCheckout={onCheckout} checkingOut={checkoutState === "good"}   onLeadSubmit={onLeadSubmit} leadStatus={leadStatuses?.good}   leadEmail={leadEmail} onOpenConsult={onOpenConsult} />
        <TierCard tierKey="better" data={result.tiers?.better} onCheckout={onCheckout} checkingOut={checkoutState === "better"} onLeadSubmit={onLeadSubmit} leadStatus={leadStatuses?.better} leadEmail={leadEmail} onOpenConsult={onOpenConsult} />
        <TierCard tierKey="best"   data={result.tiers?.best}   onCheckout={onCheckout} checkingOut={checkoutState === "best"}   onLeadSubmit={onLeadSubmit} leadStatus={leadStatuses?.best}   leadEmail={leadEmail} onOpenConsult={onOpenConsult} />
      </div>

      <div className="flex flex-col items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="share-btn"
            onClick={onShare}
            disabled={shareState.loading}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-blue-400 hover:text-blue-700 disabled:cursor-not-allowed"
          >{shareState.loading ? "Creating link…" : "🔗 Share my setup"}</button>
          {shareState.url && (
            <div className="flex items-center gap-2 rounded-md bg-blue-50 px-3 py-1.5 text-xs text-blue-800" data-testid="share-url-box">
              <code className="max-w-[160px] truncate sm:max-w-[260px]">{shareState.url}</code>
              <button
                data-testid="share-copy"
                onClick={() => navigator.clipboard?.writeText(shareState.url)}
                className="font-semibold text-blue-700 hover:underline"
              >{shareState.copied ? "Copied!" : "Copy"}</button>
            </div>
          )}
          {shareState.error && (
            <span className="text-xs text-red-600" data-testid="share-error">{shareState.error}</span>
          )}
        </div>
        <button data-testid="restart-btn" onClick={onRestart} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
          ← Start over
        </button>
      </div>

      <details className="rounded-lg border border-slate-200 bg-white p-4">
        <summary className="cursor-pointer text-sm font-semibold text-slate-700">Raw JSON (debug)</summary>
        <pre data-testid="raw-json" className="mt-3 max-h-96 overflow-auto rounded bg-slate-900 p-3 text-xs text-slate-100">
          {JSON.stringify(result, null, 2)}
        </pre>
      </details>
    </div>
  );
}

// ============== App ==============
function Wizard() {
  const [step, setStep] = useState(1);
  const [groups, setGroups] = useState([]);
  const [appliancesError, setAppliancesError] = useState(null);
  const [selections, setSelections] = useState({});
  const [sunHours, setSunHours] = useState(5);
  const [backupDays, setBackupDays] = useState(1);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [shareState, setShareState] = useState({ loading: false, url: null, error: null, copied: false });
  const [checkoutState, setCheckoutState] = useState(null);
  const [hydratingFromShare, setHydratingFromShare] = useState(false);
  const [leadStatuses, setLeadStatuses] = useState({}); // {tierKey: 'idle'|'submitting'|'done'|'error'}
  const [consultModalOpen, setConsultModalOpen] = useState(false);
  const [leadEmail, setLeadEmail] = useState(() => {
    try { return localStorage.getItem("sizer_lead_email") || ""; } catch (_) { return ""; }
  });

  // Load appliances — never blank-page the user: try API → retry once → fall
  // back to the bundled list (slightly stale but functional). The funnel
  // MUST stay openable even when the backend is degraded (CDN edge errors,
  // origin 5xx, network blips), especially for paid-ad traffic.
  useEffect(() => {
    let cancelled = false;
    const fallbackGroups = APPLIANCES_FALLBACK?.groups || [];
    async function attempt(retried) {
      try {
        const r = await axios.get(`${API}/appliances`, { timeout: 8000 });
        if (cancelled) return;
        const g = r.data?.groups || [];
        if (g.length > 0) {
          setGroups(g);
          setAppliancesError(null);
          return;
        }
        throw new Error("Empty appliance groups");
      } catch (e) {
        if (cancelled) return;
        if (!retried) {
          setTimeout(() => attempt(true), 1500);
          return;
        }
        // Final fallback: bundled list. Keep the funnel alive.
        // eslint-disable-next-line no-console
        console.warn(
          "[sizer] /api/appliances unreachable — using bundled fallback. Error:",
          e?.message,
        );
        setGroups(fallbackGroups);
        setAppliancesError(null); // never show the user a blank-funnel banner
      }
    }
    attempt(false);
    return () => { cancelled = true; };
  }, []);

  // Hydrate from ?s=<token>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("s");
    if (!token) return;
    setHydratingFromShare(true);
    axios.get(`${API}/size/share`, { params: { token } })
      .then((r) => {
        setResult(r.data.response);
        setLastRequest(r.data.request);
        setStep(3);
      })
      .catch((e) => {
        setCalcError(`Share link: ${e?.response?.data?.detail || e.message}`);
      })
      .finally(() => setHydratingFromShare(false));
  }, []);

  function addCustom({ label, watts, hours_per_day }) {
    const id = `__custom__${Date.now()}`;
    setSelections((prev) => ({
      ...prev,
      [id]: { custom: true, label, watts, quantity: 1, hours_per_day },
    }));
  }

  const buildRequest = useCallback(() => {
    const loads = Object.entries(selections).map(([k, v]) => {
      if (v.custom) {
        return { custom: true, label: v.label, watts: v.watts, quantity: v.quantity || 1, hours_per_day: v.hours_per_day || 0 };
      }
      return { key: k, quantity: v.quantity || 1, hours_per_day: v.hours_per_day || 0 };
    });
    return { loads, sun_hours: sunHours, backup_days: backupDays };
  }, [selections, sunHours, backupDays]);

  async function calculate() {
    setCalculating(true);
    setCalcError(null);
    const req = buildRequest();
    try {
      const resp = await axios.post(`${API}/size`, req);
      setResult(resp.data);
      setLastRequest(req);
      setStep(3);
      // Meta Pixel: Lead — user reached Step 3 results
      fbqTrack("Lead", {
        content_category: "sizer_completed",
        value: resp.data?.summary?.total_daily_wh || 0,
        currency: "USD",
      });
    } catch (e) {
      setCalcError(e?.response?.data?.detail || e.message);
    } finally {
      setCalculating(false);
    }
  }

  async function handleCheckout(tierKey, cart) {
    if (!cart.ready_for_checkout || !cart.lines?.length) return;
    setCheckoutState(tierKey);
    // Meta Pixel: InitiateCheckout
    fbqTrack("InitiateCheckout", {
      content_name: result?.tiers?.[tierKey]?.model || "EcoFlow bundle",
      content_category: tierKey,
      value: parseFloat(cart?.total_estimated || 0) || 0,
      currency: cart?.currency || "USD",
    });
    try {
      const resp = await axios.post(`${API}/checkout`, {
        tier: tierKey,
        lines: cart.lines,
        context: {
          total_wh: result.summary.total_daily_wh,
          peak_w: result.summary.peak_continuous_w,
          sun_hours: result.summary.sun_hours,
          backup_days: result.summary.backup_days,
        },
      });
      window.location.href = resp.data.checkoutUrl;
    } catch (e) {
      setCalcError(`Checkout: ${e?.response?.data?.detail || e.message}`);
      setCheckoutState(null);
    }
  }

  async function handleShare() {
    if (!result || !lastRequest) return;
    setShareState({ loading: true, url: null, error: null, copied: false });
    try {
      const resp = await axios.post(`${API}/size/share`, { request: lastRequest, response: result });
      const url = resp.data.url || `${window.location.origin}/?s=${resp.data.token}`;
      setShareState({ loading: false, url, error: null, copied: false });
      try {
        await navigator.clipboard?.writeText(url);
        setShareState((s) => ({ ...s, copied: true }));
      } catch (_) { /* no-op */ }
    } catch (e) {
      setShareState({ loading: false, url: null, error: e?.response?.data?.detail || e.message, copied: false });
    }
  }

  async function handleLeadSubmit(tierKey, email) {
    setLeadStatuses((p) => ({ ...p, [tierKey]: "submitting" }));
    try {
      await axios.post(`${API}/leads/notify-when-available`, {
        email,
        tier: tierKey,
        sizer_request: lastRequest || {},
        sizer_response: result || {},
      });
      try { localStorage.setItem("sizer_lead_email", email); } catch (_) { /* no-op */ }
      setLeadEmail(email);
      setLeadStatuses((p) => ({ ...p, [tierKey]: "done" }));
      // Meta Pixel: Subscribe — out-of-stock notify-me lead
      fbqTrack("Subscribe", { content_category: "out_of_stock_lead" });
    } catch (e) {
      setLeadStatuses((p) => ({ ...p, [tierKey]: "error" }));
    }
  }

  function handleOpenConsult(tierKey, model) {
    setConsultModalOpen(true);
    // Meta Pixel: Schedule — Ocean Pro consultation modal opened
    fbqTrack("Schedule", { content_category: "ocean_pro_consultation" });
    // Server-side analytics event (best-effort, fire-and-forget)
    axios
      .post(`${API}/events`, {
        type: "consultation_modal_opened",
        payload: { tier: tierKey, model: model || "EcoFlow Ocean Pro" },
      })
      .catch(() => { /* swallow — analytics must not impact UX */ });
  }

  function restart() {
    setStep(1);
    setResult(null);
    setSelections({});
    setShareState({ loading: false, url: null, error: null, copied: false });
    if (window.location.search) window.history.replaceState({}, "", window.location.pathname);
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4">
          <div className="min-w-0">
            <div
              className="text-[10px] font-extrabold uppercase tracking-[0.18em] sm:text-[11px] sm:tracking-[0.22em]"
              style={{
                background: `linear-gradient(135deg, ${BRAND}, ${BRAND_CYAN})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Starpower Network
            </div>
            <h1 className="truncate text-base font-extrabold text-slate-900 sm:text-xl" data-testid="app-title">
              EcoFlow Product Sizer
            </h1>
          </div>
          <a
            className="shrink-0 rounded-full px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:shadow sm:text-sm"
            href="https://shop.starpower.network"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="shop-cta"
            style={{ background: `linear-gradient(135deg, ${BRAND}, ${BRAND_CYAN})` }}
          >Shop at Starpower ↗</a>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-6 py-6">
        <Stepper step={step} />
      </div>

      <main className="mx-auto max-w-5xl px-6 pb-16">
        {hydratingFromShare && (
          <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800" data-testid="hydrating">
            Loading shared setup…
          </div>
        )}
        {appliancesError && (
          <div data-testid="appliances-error" className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            Failed to load appliances: {appliancesError}
          </div>
        )}
        {calcError && (
          <div data-testid="calc-error" className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {calcError}
          </div>
        )}

        {step === 1 && (
          <ApplianceStep
            groups={groups}
            selections={selections}
            setSelections={setSelections}
            onNext={() => setStep(2)}
            onOpenCustom={() => setCustomOpen(true)}
          />
        )}
        {step === 2 && (
          <SolarStep
            sunHours={sunHours}
            setSunHours={setSunHours}
            backupDays={backupDays}
            setBackupDays={setBackupDays}
            onBack={() => setStep(1)}
            onCalc={calculate}
            calculating={calculating}
          />
        )}
        {step === 3 && (
          <ResultsStep
            result={result}
            onRestart={restart}
            onShare={handleShare}
            shareState={shareState}
            onCheckout={handleCheckout}
            checkoutState={checkoutState}
            onLeadSubmit={handleLeadSubmit}
            leadStatuses={leadStatuses}
            leadEmail={leadEmail}
            onOpenConsult={handleOpenConsult}
          />
        )}
      </main>

      <CustomModal open={customOpen} onClose={() => setCustomOpen(false)} onAdd={addCustom} />
      <ConsultModal open={consultModalOpen} onClose={() => setConsultModalOpen(false)} />

      <footer className="border-t border-slate-200 bg-white py-6 text-center text-xs text-slate-400">
        Powered by Starpower Network · EcoFlow products via{" "}
        <a href="https://shop.starpower.network" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
          shop.starpower.network
        </a>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Wizard />} />
        <Route path="/admin" element={<AdminDashboard />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
